import type { MouseEvent, ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { useMediaQuery, useTheme } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

import { EMPTY_OBJECT } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { AlertMessages } from '../AlertMessages';
import { MessageList } from '../MessageList';

import { FileCategoryList } from './components/FileCategoryList';
import { FilesConsultation } from './components/FilesConsultation';
import { FilesExplorerGrid } from './components/FilesExplorerGrid';
import { FilesUpload } from './components/FilesUpload';
import { FileExplorerStrategy } from './enums/strategy.enum';
import { useFilesPageState } from './hooks/useFilesPageState.hook';
import type { Mode } from './types/mode.type';
import type { FileCategory } from './types/file-category.type';
import type { FileItem } from './types/file-item.type';
import type { FileCategoryAccesses } from './types/file-category-access.type';
import {
  fileCategoryAccessOf,
  modeGateOf,
} from './utils/file-category-access.util';

export { FileExplorerStrategy, useFilesPageState };
export type { FileCategory, FileItem };

interface BaseProps {
  readonly allowedFileCategories: readonly FileCategory[];
  // The access to the files of each category: every category is open without it.
  readonly fileCategoryAccesses?: FileCategoryAccesses;
  readonly fileCountsByCategoryId?: ReadonlyMap<Id, number>;
  readonly isLoadingCategories: boolean;
  readonly files: readonly FileItem[];
  readonly isLoadingFiles: boolean;
  readonly isDeletingFiles?: boolean;
  readonly isMovingFiles?: boolean;
  readonly isRenamingFile?: boolean;
  readonly entityId: Id;
  readonly tusCategory: string;
  readonly entityLabel?: string;
  readonly filesQueryDocumentNamesToInvalidate: readonly string[];
  readonly onDeleteFiles: (fileIds: readonly Id[]) => Promise<void>;
  readonly onMoveFiles: (
    params: Readonly<{
      fileIds: readonly Id[];
      destinationFileCategoryId: Id;
    }>,
  ) => Promise<void>;
  readonly onRenameFile: (
    params: Readonly<{ fileId: Id; newOriginalName: string }>,
  ) => Promise<void>;
}

interface StatefulProps extends BaseProps {
  readonly strategy: FileExplorerStrategy.Stateful;
}

interface ControlledProps extends BaseProps {
  readonly strategy: FileExplorerStrategy.Controlled;
  readonly selectedFileCategoryId: Id | null;
  readonly mode: Mode;
  readonly onCategoryChange: (id: Id) => void;
  readonly onModeChange: (mode: Mode) => void;
}

interface RouterConnectedProps extends BaseProps {
  readonly strategy: FileExplorerStrategy.RouterConnected;
  readonly buildPath: (
    fileCategoryId: Id,
    mode: Mode,
    fileId?: Id | null,
  ) => string;
}

type FileExplorerProps = StatefulProps | ControlledProps | RouterConnectedProps;

export const FileExplorer = (props: Readonly<FileExplorerProps>) => {
  const {
    allowedFileCategories,
    fileCategoryAccesses,
    fileCountsByCategoryId,
    files,
    isLoadingCategories,
    isLoadingFiles,
    isDeletingFiles,
    isMovingFiles,
    isRenamingFile,
    entityId,
    tusCategory,
    entityLabel,
    filesQueryDocumentNamesToInvalidate,
    onDeleteFiles,
    onMoveFiles,
    onRenameFile,
    strategy,
  } = props;

  // Always called - like Tabs calls useLocation() regardless of strategy.
  const {
    fileCategoryId: urlFileCategoryId,
    mode: urlMode,
    fileId: urlFileId,
  } = useParams<{
    fileCategoryId?: string;
    mode?: string;
    fileId?: string;
  }>();
  const navigate = useNavigate();

  const [statefulCategoryId, setStatefulCategoryId] = useState<Id | null>(null);
  const [statefulMode, setStatefulMode] = useState<Mode>('content');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const visibleFileCategories = useMemo(
    () => allowedFileCategories.filter((c) => !c.hideInFileExplorer),
    [allowedFileCategories],
  );

  const selectedFileCategoryId: Id | null =
    strategy === FileExplorerStrategy.RouterConnected
      ? ((urlFileCategoryId as Id | undefined) ?? null)
      : strategy === FileExplorerStrategy.Controlled
        ? // eslint-disable-next-line react/destructuring-assignment
          props.selectedFileCategoryId
        : (statefulCategoryId ?? visibleFileCategories[0]?._id ?? null);

  const mode: Mode =
    strategy === FileExplorerStrategy.RouterConnected
      ? urlMode === 'upload' || urlMode === 'gallery'
        ? urlMode
        : 'content'
      : strategy === FileExplorerStrategy.Controlled
        ? // eslint-disable-next-line react/destructuring-assignment
          props.mode
        : statefulMode;

  const handleCategoryChange = useCallback(
    (id: Id) => {
      if (strategy === FileExplorerStrategy.Stateful) {
        setStatefulCategoryId(id);
      } else if (strategy === FileExplorerStrategy.Controlled) {
        // eslint-disable-next-line react/destructuring-assignment
        props.onCategoryChange(id);
      } else {
        // eslint-disable-next-line react/destructuring-assignment
        void navigate(props.buildPath(id, mode));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, mode, navigate],
  );

  const setMode = useCallback(
    (newMode: Mode) => {
      if (strategy === FileExplorerStrategy.Stateful) {
        setStatefulMode(newMode);
      } else if (strategy === FileExplorerStrategy.Controlled) {
        // eslint-disable-next-line react/destructuring-assignment
        props.onModeChange(newMode);
      } else if (selectedFileCategoryId) {
        // eslint-disable-next-line react/destructuring-assignment
        void navigate(props.buildPath(selectedFileCategoryId, newMode));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, selectedFileCategoryId, navigate],
  );

  const handleModeChange = useCallback(
    (_: MouseEvent<HTMLElement>, newMode: Mode | null) => {
      if (newMode) {
        setMode(newMode);
      }
    },
    [setMode],
  );

  const switchToUploadMode = useCallback(() => {
    setMode('upload');
  }, [setMode]);

  const handlePreviewChange = useCallback(
    (fileId: Id | null) => {
      if (
        strategy !== FileExplorerStrategy.RouterConnected ||
        !selectedFileCategoryId
      ) {
        return;
      }
      // eslint-disable-next-line react/destructuring-assignment
      void navigate(props.buildPath(selectedFileCategoryId, mode, fileId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, selectedFileCategoryId, mode, navigate],
  );

  const selectedAccess = fileCategoryAccessOf(
    fileCategoryAccesses,
    selectedFileCategoryId,
  );
  // A mode reached while refused (its url opened directly) tells why, centrally.
  const modeGate = modeGateOf(selectedAccess, mode);
  const showContent =
    mode === 'content' && selectedFileCategoryId && modeGate.isAllowed;
  const showGallery =
    mode === 'gallery' && selectedFileCategoryId && modeGate.isAllowed;

  const renderModeButton = (value: Mode, icon: ReactNode, label: string) => {
    const gate = modeGateOf(selectedAccess, value);

    return (
      <Tooltip
        key={value}
        title={gate.isAllowed ? '' : <MessageList messages={gate.reasons} />}
      >
        <span>
          <ToggleButton
            disabled={!gate.isAllowed}
            onChange={handleModeChange}
            selected={mode === value}
            size="small"
            value={value}
          >
            {icon}
            {label}
          </ToggleButton>
        </span>
      </Tooltip>
    );
  };

  const selectedFileCategoryLabel = useMemo(
    () =>
      allowedFileCategories.find((c) => c._id === selectedFileCategoryId)
        ?.label,
    [allowedFileCategories, selectedFileCategoryId],
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        flexGrow: 1,
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          borderBottom: isMobile ? 1 : 0,
          borderColor: 'divider',
          borderRight: isMobile ? 0 : 1,
          width: isMobile ? '100%' : 220,
          ...(isMobile ? EMPTY_OBJECT : { minWidth: 220 }),
        }}
      >
        <FileCategoryList
          accesses={fileCategoryAccesses}
          categories={visibleFileCategories}
          fileCountsByCategoryId={fileCountsByCategoryId}
          isLoading={isLoadingCategories}
          onSelect={handleCategoryChange}
          selectedId={selectedFileCategoryId}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-end',
            px: 2,
            py: 1,
          }}
        >
          <ToggleButtonGroup
            color="primary"
            exclusive
            size="small"
            sx={
              isMobile
                ? {
                    width: '100%',
                    '& > span': { flex: 1 },
                    '& .MuiToggleButton-root': { width: '100%' },
                  }
                : undefined
            }
            value={mode}
          >
            {renderModeButton(
              'content',
              <FolderOpenIcon fontSize="small" sx={{ mr: 0.5 }} />,
              'Contenu',
            )}
            {renderModeButton(
              'gallery',
              <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />,
              'Galerie',
            )}
            {renderModeButton(
              'upload',
              <UploadFileIcon fontSize="small" sx={{ mr: 0.5 }} />,
              'Upload',
            )}
          </ToggleButtonGroup>
        </Box>

        <Divider />

        <Box
          sx={{
            ...((!showContent && !showGallery) || !files.length
              ? { margin: 'auto' }
              : {}),
            overflow: 'auto',
            p: 2,
            maxWidth: '100%',
          }}
        >
          {selectedFileCategoryId && !modeGate.isAllowed ? (
            <AlertMessages
              messages={modeGate.reasons}
              severity="warning"
              title="Accès limité"
            />
          ) : showContent ? (
            <FilesConsultation
              allowedFileCategories={visibleFileCategories}
              files={files}
              isDeletingFiles={isDeletingFiles}
              isLoading={isLoadingFiles}
              isMovingFiles={isMovingFiles}
              isRenamingFile={isRenamingFile}
              onDeleteFiles={onDeleteFiles}
              onMoveFiles={onMoveFiles}
              onRenameFile={onRenameFile}
              onUploadClick={switchToUploadMode}
              selectedFileCategoryId={selectedFileCategoryId}
              uploadGate={selectedAccess.upload}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(strategy === FileExplorerStrategy.RouterConnected
                ? {
                    previewFileId: (urlFileId as Id | undefined) ?? null,
                    onPreviewChange: handlePreviewChange,
                  }
                : {})}
            />
          ) : showGallery ? (
            <FilesExplorerGrid
              allowedFileCategories={visibleFileCategories}
              files={files}
              isDeletingFiles={isDeletingFiles}
              isLoading={isLoadingFiles}
              isMovingFiles={isMovingFiles}
              isRenamingFile={isRenamingFile}
              onDeleteFiles={onDeleteFiles}
              onMoveFiles={onMoveFiles}
              onRenameFile={onRenameFile}
              onUploadClick={switchToUploadMode}
              selectedFileCategoryId={selectedFileCategoryId}
              uploadGate={selectedAccess.upload}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(strategy === FileExplorerStrategy.RouterConnected
                ? {
                    previewFileId: (urlFileId as Id | undefined) ?? null,
                    onPreviewChange: handlePreviewChange,
                  }
                : {})}
            />
          ) : selectedFileCategoryId ? (
            <FilesUpload
              entityId={entityId}
              entityLabel={entityLabel}
              fileCategoryId={selectedFileCategoryId}
              fileCategoryLabel={selectedFileCategoryLabel}
              queryDocumentNamesToInvalidate={
                filesQueryDocumentNamesToInvalidate
              }
              tusCategory={tusCategory}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
